import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Breadcrumb from '../../components/Breadcrumb';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';
import {
  AICallLogItem,
  AICallLogFilter,
  AICallType,
  AICallStatus,
} from '../../types/aiCallLog';
import { listAICallLogs, getFacets, AICallLogFacets } from '../../api/aiCallLog';
import AICallDrawer from '../../components/admin/AICallDrawer';
import AICallEpisodeLink from '../../components/admin/AICallEpisodeLink';
import './AICallLogs.css';

const PAGE_SIZE = 20;
const REFRESH_INTERVAL_MS = 10_000;

function typeLabel(t: TFunction, kind: AICallType): string {
  switch (kind) {
    case 'llm':
      return t('aiCallLogs.types.llm', { defaultValue: 'LLM' });
    case 'image':
      return t('aiCallLogs.types.image', { defaultValue: '图片' });
    case 'video':
      return t('aiCallLogs.types.video', { defaultValue: '视频' });
    case 'voice':
      return t('aiCallLogs.types.voice', { defaultValue: '语音' });
    case 'external':
      return t('aiCallLogs.types.external', { defaultValue: 'API' });
    default:
      return kind;
  }
}

function statusLabel(t: TFunction, status: AICallStatus): string {
  switch (status) {
    case 'submitted':
      return t('aiCallLogs.statuses.submitted', { defaultValue: '已提交' });
    case 'success':
      return t('aiCallLogs.statuses.success', { defaultValue: '成功' });
    case 'failed':
      return t('aiCallLogs.statuses.failed', { defaultValue: '失败' });
    default:
      return status;
  }
}

function stepLabel(t: TFunction, step: string): string {
  if (step === 'analysis.director_mark_mapping') {
    return t('aiCallLogs.steps.directorMarkMapping', { defaultValue: '导演标注映射' });
  }
  return step;
}

interface ListData {
  items: AICallLogItem[];
  total: number;
}

const INITIAL_FILTER: AICallLogFilter = { page: 1, pageSize: PAGE_SIZE };

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  return fallback;
}

interface BadgeProps {
  kind: 'type' | 'status';
  value: AICallType | AICallStatus;
  label: string;
}

function Badge({ kind, value, label }: BadgeProps) {
  return <span className={`ai-call-badge ${kind}-${value}`}>{label}</span>;
}

function formatDuration(ms: number, status: AICallStatus): string {
  if (status === 'submitted') return '—';
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export default function AICallLogs() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [draft, setDraft] = useState<AICallLogFilter>(INITIAL_FILTER);
  const [filter, setFilter] = useState<AICallLogFilter>(INITIAL_FILTER);
  const [data, setData] = useState<ListData>({ items: [], total: 0 });
  const [selected, setSelected] = useState<AICallLogItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState<AICallLogFacets>({ platforms: [], steps: [] });
  const [jumpPage, setJumpPage] = useState('');

  useEffect(() => {
    let cancelled = false;
    getFacets()
      .then((f) => {
        if (!cancelled) setFacets(f);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = useCallback(
    async (silent = false): Promise<void> => {
      if (!silent) setLoading(true);
      try {
        const result = await listAICallLogs(filter);
        setData(result);
      } catch (error: unknown) {
        if (!silent) toast.error(getErrorMessage(error, '加载失败'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [filter, toast],
  );

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const timer = setInterval(() => {
      refetch(true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refetch]);

  const setField = <K extends keyof AICallLogFilter>(
    key: K,
    value: AICallLogFilter[K],
  ): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const search = (): void => setFilter({ ...draft, page: 1 });

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') search();
  };

  const goToPage = (page: number): void => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const pageSize = filter.pageSize ?? PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const currentPage = filter.page ?? 1;

  const goToJumpPage = (): void => {
    // 仅接受纯正整数：parseInt 会把 "3.9"/"3abc" 截断成 3 静默跳走，整体拒绝更安全
    const trimmed = jumpPage.trim();
    if (!/^\d+$/.test(trimmed)) return;
    goToPage(Math.min(totalPages, Math.max(1, Number(trimmed))));
    setJumpPage('');
  };

  const onJumpPageKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') goToJumpPage();
  };

  const reset = (): void => {
    setDraft(INITIAL_FILTER);
    setFilter(INITIAL_FILTER);
  };

  return (
    <div className="ai-call-logs-shell">
      <Breadcrumb
        items={[
          { label: t('projects.breadcrumbHome'), to: '/projects' },
          { label: t('aiCallLogs.title', { defaultValue: '调用审计' }) },
        ]}
      />

      <div className="ai-call-logs-header">
        <h1 className="page-title ai-call-logs-title">
          {t('aiCallLogs.title', { defaultValue: '调用审计' })}
        </h1>
        <div className="ai-call-logs-header-actions">
          <button className="btn" onClick={() => refetch()} disabled={loading}>
            {loading
              ? t('aiCallLogs.actions.loading', { defaultValue: '加载中…' })
              : t('aiCallLogs.actions.refresh', { defaultValue: '刷新' })}
          </button>
        </div>
      </div>

      <div className="ai-call-logs-filters">
        <div className="ai-call-logs-filter-row">
          <div className="ai-call-logs-filter-field">
            <label className="ai-call-logs-filter-label" htmlFor="filter-project">
              {t('aiCallLogs.filter.projectId', { defaultValue: '项目 ID' })}
            </label>
            <input
              id="filter-project"
              placeholder={t('aiCallLogs.filter.projectIdPlaceholder', { defaultValue: '项目 ID' })}
              value={draft.projectId ?? ''}
              onChange={(e) => setField('projectId', e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          <div className="ai-call-logs-filter-field">
            <label className="ai-call-logs-filter-label" htmlFor="filter-type">
              {t('aiCallLogs.filter.type', { defaultValue: '类型' })}
            </label>
            <select
              id="filter-type"
              value={(draft.callType as string) ?? ''}
              onChange={(e) =>
                setField('callType', (e.target.value || undefined) as AICallType | undefined)
              }
            >
              <option value="">{t('aiCallLogs.filter.allTypes', { defaultValue: '全部类型' })}</option>
              <option value="llm">{typeLabel(t, 'llm')}</option>
              <option value="image">{typeLabel(t, 'image')}</option>
              <option value="video">{typeLabel(t, 'video')}</option>
              <option value="voice">{typeLabel(t, 'voice')}</option>
              <option value="external">{typeLabel(t, 'external')}</option>
            </select>
          </div>
          <div className="ai-call-logs-filter-field">
            <label className="ai-call-logs-filter-label" htmlFor="filter-status">
              {t('aiCallLogs.filter.status', { defaultValue: '状态' })}
            </label>
            <select
              id="filter-status"
              value={(draft.status as string) ?? ''}
              onChange={(e) =>
                setField('status', (e.target.value || undefined) as AICallStatus | undefined)
              }
            >
              <option value="">
                {t('aiCallLogs.filter.allStatuses', { defaultValue: '全部状态' })}
              </option>
              <option value="submitted">{statusLabel(t, 'submitted')}</option>
              <option value="success">{statusLabel(t, 'success')}</option>
              <option value="failed">{statusLabel(t, 'failed')}</option>
            </select>
          </div>
          <div className="ai-call-logs-filter-field">
            <label className="ai-call-logs-filter-label" htmlFor="filter-platform">
              {t('aiCallLogs.filter.platform', { defaultValue: '平台' })}
            </label>
            <select
              id="filter-platform"
              value={draft.platform ?? ''}
              onChange={(e) => setField('platform', e.target.value || undefined)}
            >
              <option value="">
                {t('aiCallLogs.filter.allPlatforms', { defaultValue: '全部平台' })}
              </option>
              {facets.platforms.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="ai-call-logs-filter-field">
            <label className="ai-call-logs-filter-label" htmlFor="filter-step">
              {t('aiCallLogs.filter.step', { defaultValue: 'Step' })}
            </label>
            <select
              id="filter-step"
              value={draft.step ?? ''}
              onChange={(e) => setField('step', e.target.value || undefined)}
            >
              <option value="">
                {t('aiCallLogs.filter.allSteps', { defaultValue: '全部 Step' })}
              </option>
              {facets.steps.map((s) => (
                <option key={s} value={s}>{stepLabel(t, s)}</option>
              ))}
            </select>
          </div>
          <div className="ai-call-logs-filter-field">
            <label className="ai-call-logs-filter-label" htmlFor="filter-operator">
              {t('aiCallLogs.filter.operatorId', { defaultValue: '操作者 ID' })}
            </label>
            <input
              id="filter-operator"
              placeholder={t('aiCallLogs.filter.operatorIdPlaceholder', { defaultValue: '用户 ID' })}
              value={draft.operatorId ?? ''}
              onChange={(e) => setField('operatorId', e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>
        </div>
        <div className="ai-call-logs-filter-actions">
          <button className="btn btn-secondary" onClick={reset} disabled={loading}>
            {t('aiCallLogs.actions.reset', { defaultValue: '重置' })}
          </button>
          <button className="btn btn-primary" onClick={search} disabled={loading}>
            {t('aiCallLogs.actions.search', { defaultValue: '搜索' })}
          </button>
        </div>
      </div>

      <div className="ai-call-logs-panel">
        <div className="ai-call-logs-table-wrap">
          <table className="ai-call-logs-table">
            <thead>
              <tr>
                <th>{t('aiCallLogs.table.time', { defaultValue: '时间' })}</th>
                <th>{t('aiCallLogs.table.project', { defaultValue: '项目' })}</th>
                <th>{t('aiCallLogs.table.episode', { defaultValue: '分集' })}</th>
                <th>{t('aiCallLogs.table.type', { defaultValue: '类型' })}</th>
                <th>{t('aiCallLogs.table.platformModel', { defaultValue: '平台 / 模型' })}</th>
                <th>{t('aiCallLogs.table.step', { defaultValue: 'Step' })}</th>
                <th>{t('aiCallLogs.table.status', { defaultValue: '状态' })}</th>
                <th>{t('aiCallLogs.table.http', { defaultValue: 'HTTP' })}</th>
                <th>{t('aiCallLogs.table.duration', { defaultValue: '耗时' })}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && !loading ? (
                <tr>
                  <td className="ai-call-logs-empty" colSpan={9}>
                    {t('aiCallLogs.table.empty', { defaultValue: '暂无调用记录' })}
                  </td>
                </tr>
              ) : (
                data.items.map((it) => (
                  <tr
                    key={it.id}
                    className={it.status === 'failed' ? 'is-failed' : ''}
                    onClick={() => setSelected(it)}
                  >
                    <td className="ai-call-logs-cell-time">{formatDate(it.createdAt)}</td>
                    <td>
                      {it.projectId ? (
                        <a
                          href={`/projects/${it.projectId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <code>{it.projectId}</code>
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      <AICallEpisodeLink item={it} stopPropagation />
                    </td>
                    <td>
                      <Badge kind="type" value={it.callType} label={typeLabel(t, it.callType)} />
                    </td>
                    <td>
                      <div>{it.platform || '-'}</div>
                      {it.model && <div className="ai-call-logs-cell-meta">{it.model}</div>}
                    </td>
                    <td>{it.step ? <code>{stepLabel(t, it.step)}</code> : '-'}</td>
                    <td>
                      <Badge kind="status" value={it.status} label={statusLabel(t, it.status)} />
                    </td>
                    <td>{it.httpStatus || '-'}</td>
                    <td>{formatDuration(it.durationMs, it.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="ai-call-logs-footer">
          <div className="ai-call-logs-pagination-info">
            {t('aiCallLogs.pagination.info', {
              total: data.total,
              page: currentPage,
              totalPages,
              defaultValue: '共 {{total}} 条 · 第 {{page}} / {{totalPages}} 页',
            })}
          </div>
          <div className="ai-call-logs-pagination">
            <button
              className="btn"
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1 || loading}
            >
              {t('aiCallLogs.actions.prevPage', { defaultValue: '上一页' })}
            </button>
            <button
              className="btn"
              onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              {t('aiCallLogs.actions.nextPage', { defaultValue: '下一页' })}
            </button>
            <div className="ai-call-logs-page-jump">
              <label htmlFor="ai-call-logs-page-jump-input">
                {t('aiCallLogs.pagination.jumpLabel', { defaultValue: '跳到' })}
              </label>
              <input
                id="ai-call-logs-page-jump-input"
                type="number"
                min={1}
                max={totalPages}
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={onJumpPageKeyDown}
                disabled={loading}
              />
              <button
                className="btn"
                onClick={goToJumpPage}
                disabled={loading || jumpPage.trim() === ''}
              >
                {t('aiCallLogs.pagination.jumpAction', { defaultValue: '跳转' })}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AICallDrawer
        item={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
